import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';

const STATUS_LABELS = {
  confirmed: 'Confirmada', attended: 'Atendida',
  not_attended: 'No asistió', cancelled: 'Cancelada', rescheduled: 'Reagendada',
};
const STATUS_COLOR = {
  confirmed: '#2563eb', attended: '#16a34a',
  not_attended: '#64748b', cancelled: '#dc2626', rescheduled: '#8b5cf6',
};

const AVATAR_COLORS = ['#2563eb', '#16a34a', '#d97706', '#8b5cf6', '#db2777', '#0891b2'];
const initials = name => (name || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
const avatarColor = name => AVATAR_COLORS[((name || '').length) % AVATAR_COLORS.length];

const fmtCurrency = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

export default function AppointmentsPage() {
  const [monthAppts, setMonthAppts] = useState({});
  const [dayList, setDayList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toDateStr(new Date()));
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [workers, setWorkers] = useState([]);
  const [services, setServices] = useState([]);
  const [filterWorker, setFilterWorker] = useState('');

  const [detailModal, setDetailModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [obsModal, setObsModal] = useState(null);
  const [obsText, setObsText] = useState('');
  const [obsPayment, setObsPayment] = useState('Efectivo');
  const [newModal, setNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ client_name: '', client_phone: '', worker_id: '', service_id: '', start_time: '' });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const todayStr = toDateStr(new Date());

  // Cita del día (para acciones y lista)
  const loadDay = useCallback(() => {
    setLoading(true);
    const params = { date: selectedDate };
    if (filterWorker) params.worker_id = filterWorker;
    api.get('/appointments', { params })
      .then(res => setDayList(res.data))
      .finally(() => setLoading(false));
  }, [selectedDate, filterWorker]);

  // Mes para el mini-calendario
  const loadMonth = useCallback(async (year, month) => {
    try {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      const res = await api.get('/appointments/range', { params: { start, end } });
      const byDate = {};
      (res.data || []).forEach(a => {
        const d = a.appointment_date?.slice(0, 10);
        if (!d) return;
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(a);
      });
      setMonthAppts(byDate);
    } catch { setMonthAppts({}); }
  }, []);

  useEffect(() => { loadDay(); }, [loadDay]);
  useEffect(() => { loadMonth(calYear, calMonth); }, [calYear, calMonth, loadMonth]);

  useEffect(() => {
    api.get('/workers').then(r => setWorkers(r.data)).catch(() => {});
    api.get('/services').then(r => setServices(r.data)).catch(() => {});
  }, []);

  const fetchSlots = useCallback(async () => {
    if (!newForm.worker_id || !newForm.service_id) return;
    try {
      const res = await api.get('/appointments/availability', {
        params: { date: selectedDate, worker_id: newForm.worker_id, service_id: newForm.service_id, business_slug: '' }
      });
      setAvailableSlots(res.data.slots || []);
    } catch { setAvailableSlots([]); }
  }, [newForm.worker_id, newForm.service_id, selectedDate]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const refresh = () => { loadDay(); loadMonth(calYear, calMonth); };

  const PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'QR', 'Cupón', 'Otro'];

  const changeStatus = async (id, status, reason, observations, paymentMethod) => {
    await api.patch(`/appointments/${id}/status`, { status, cancellation_reason: reason, observations, payment_method: paymentMethod });
    refresh();
  };

  const handleObsConfirm = async () => {
    if (!obsModal) return;
    await changeStatus(obsModal.appt.id, obsModal.nextStatus, '', obsText, obsModal.nextStatus === 'attended' ? obsPayment : undefined);
    setObsModal(null); setObsText(''); setObsPayment('Efectivo');
  };

  const handleReschedule = async () => {
    if (!rescheduleDate) return;
    await api.patch(`/appointments/${rescheduleModal.id}/status`, { status: 'rescheduled', new_date: rescheduleDate });
    setRescheduleModal(null); setRescheduleDate(''); refresh();
  };

  const handleNewSubmit = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const clientRes = await api.post('/clients', { name: newForm.client_name, phone: newForm.client_phone });
      await api.post('/appointments', {
        client_id: clientRes.data.id, worker_id: newForm.worker_id,
        service_id: newForm.service_id, appointment_date: selectedDate, start_time: newForm.start_time,
      });
      setNewModal(false);
      setNewForm({ client_name: '', client_phone: '', worker_id: '', service_id: '', start_time: '' });
      refresh();
    } catch (err) { setError(err.response?.data?.error || 'Error al crear cita'); }
    finally { setSaving(false); }
  };

  const goMonth = dir => {
    let y = calYear, m = calMonth + dir;
    if (m < 0) { y--; m = 11; } else if (m > 11) { y++; m = 0; }
    setCalYear(y); setCalMonth(m);
  };

  const shiftDay = dir => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + dir);
    setSelectedDate(toDateStr(d));
    setCalYear(d.getFullYear()); setCalMonth(d.getMonth());
  };

  const dayAppts = dayList.slice().sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

  const calDays = (() => {
    const first = new Date(calYear, calMonth, 1).getDay();
    const total = new Date(calYear, calMonth + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < first; i++) arr.push(null);
    for (let i = 1; i <= total; i++) arr.push(i);
    return arr;
  })();

  const summary = {
    total: dayAppts.length,
    confirmed: dayAppts.filter(a => a.status === 'confirmed').length,
    attended: dayAppts.filter(a => a.status === 'attended').length,
    cancelled: dayAppts.filter(a => a.status === 'cancelled').length,
  };
  const nextAppt = dayAppts.find(a => a.status === 'confirmed');

  const fmtLongDate = ds => new Date(ds + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Citas programadas</h2>
          <p>Gestiona las citas del día</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="form-select" style={{ width: 'auto' }} value={filterWorker} onChange={e => setFilterWorker(e.target.value)}>
            <option value="">Todos los barberos</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.nombre || w.name}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => setNewModal(true)}>+ Nueva Cita</button>
        </div>
      </div>

      <div className="agenda-layout">
        {/* ─── Agenda principal ─── */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-ghost btn-icon" onClick={() => shiftDay(-1)}>‹</button>
              <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>📅 {fmtLongDate(selectedDate)}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => shiftDay(1)}>›</button>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedDate(todayStr); setCalYear(new Date().getFullYear()); setCalMonth(new Date().getMonth()); }}>Hoy</button>
          </div>

          <div style={{ padding: 16 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : dayAppts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                <div style={{ fontSize: 30, marginBottom: 8 }}>📭</div>
                No hay citas para este día.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {dayAppts.map(a => {
                  const color = STATUS_COLOR[a.status] || '#64748b';
                  return (
                    <div key={a.id} onClick={() => setDetailModal(a)} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                      border: '1px solid var(--color-border)', borderLeft: `4px solid ${color}`,
                      background: 'var(--color-surface)', transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ minWidth: 52, textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{a.start_time?.slice(0,5)}</div>
                        {a.end_time && <div style={{ fontSize: '0.7rem', color: 'var(--color-text-faint)' }}>{a.end_time.slice(0,5)}</div>}
                      </div>

                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        background: avatarColor(a.client_name), color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem',
                      }}>{initials(a.client_name)}</div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.client_name}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.service_name}{a.price ? ` · ${fmtCurrency(a.price)}` : ''}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          ✂️ {a.worker_name || 'Sin barbero'}
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                        <span className={`badge badge-${a.status}`}>{STATUS_LABELS[a.status]}</span>
                        <span style={{ fontSize: '1.1rem', color: 'var(--color-text-faint)' }}>›</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {dayAppts.length > 0 && (
            <div style={{ margin: 16, marginTop: 0, padding: '12px 16px', background: 'var(--color-info-bg)', borderRadius: 10, fontSize: '0.85rem', color: 'var(--color-text-muted)', display: 'flex', gap: 8 }}>
              <span>ℹ️</span>
              <span>Tienes <strong style={{ color: 'var(--color-text)' }}>{summary.total}</strong> citas programadas para este día.{nextAppt && <> Próxima a las <strong style={{ color: 'var(--color-text)' }}>{nextAppt.start_time?.slice(0,5)}</strong>.</>}</span>
            </div>
          )}
        </div>

        {/* ─── Sidebar derecha ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <button className="btn btn-ghost btn-icon" onClick={() => goMonth(-1)}>‹</button>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', textTransform: 'capitalize' }}>
                {new Date(calYear, calMonth).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}
              </span>
              <button className="btn btn-ghost btn-icon" onClick={() => goMonth(1)}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
              {['D','L','M','X','J','V','S'].map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-text-faint)', padding: '2px 0' }}>{d}</div>
              ))}
              {calDays.map((d, i) => {
                if (!d) return <div key={i} />;
                const ds = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const has = (monthAppts[ds] || []).filter(a => !filterWorker || String(a.worker_id) === String(filterWorker)).length > 0;
                const isToday = ds === todayStr;
                const isSel = ds === selectedDate;
                return (
                  <div key={i} onClick={() => setSelectedDate(ds)} style={{
                    aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', position: 'relative',
                    fontWeight: isToday || isSel ? 700 : 500,
                    background: isSel ? 'var(--color-info)' : isToday ? 'var(--color-info-bg)' : 'transparent',
                    color: isSel ? '#fff' : isToday ? 'var(--color-info)' : 'var(--color-text)',
                  }}>
                    {d}
                    {has && <span style={{ width: 4, height: 4, borderRadius: '50%', background: isSel ? '#fff' : 'var(--color-info)', position: 'absolute', bottom: 4 }} />}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <h4 style={{ marginBottom: 14, fontSize: '0.95rem' }}>Resumen del día</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['📅', summary.total, 'Citas programadas', '#2563eb'],
                ['✅', summary.confirmed, 'Confirmadas', '#16a34a'],
                ['✂️', summary.attended, 'Atendidas', '#8b5cf6'],
                ['✕', summary.cancelled, 'Canceladas', '#dc2626'],
              ].map(([icon, val, label, color]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: color + '1f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', minWidth: 22 }}>{val}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {nextAppt && (
            <div className="card">
              <h4 style={{ marginBottom: 12, fontSize: '0.95rem' }}>Próxima cita</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: avatarColor(nextAppt.client_name),
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem',
                }}>{initials(nextAppt.client_name)}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{nextAppt.client_name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    {nextAppt.start_time?.slice(0,5)} · {nextAppt.service_name}
                  </div>
                  <span className={`badge badge-${nextAppt.status}`} style={{ marginTop: 4 }}>{STATUS_LABELS[nextAppt.status]}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal detalle cita */}
      {detailModal && (
        <div className="modal-overlay" onClick={() => setDetailModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Detalle de cita</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setDetailModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Info cliente */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                  background: avatarColor(detailModal.client_name), color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem',
                }}>{initials(detailModal.client_name)}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{detailModal.client_name}</div>
                  {detailModal.client_phone && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>📞 {detailModal.client_phone}</div>}
                </div>
              </div>
              {/* Detalles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.88rem', background: 'var(--color-surface-2)', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Hora</span>
                  <span style={{ fontWeight: 600 }}>{detailModal.start_time?.slice(0,5)}{detailModal.end_time ? ` – ${detailModal.end_time.slice(0,5)}` : ''}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Servicio</span>
                  <span style={{ fontWeight: 600 }}>{detailModal.service_name}</span>
                </div>
                {detailModal.price && <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Precio</span>
                  <span style={{ fontWeight: 600 }}>{fmtCurrency(detailModal.price)}</span>
                </div>}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Barbero</span>
                  <span style={{ fontWeight: 600 }}>{detailModal.worker_name || 'Sin asignar'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Estado</span>
                  <span className={`badge badge-${detailModal.status}`}>
                    {STATUS_LABELS[detailModal.status] || detailModal.status || '—'}
                  </span>
                </div>
              </div>
              {/* Acciones — disponibles mientras no esté cancelada */}
              {detailModal.status !== 'cancelled' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {detailModal.status !== 'attended' && (
                    <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setDetailModal(null); setObsModal({ appt: detailModal, nextStatus: 'attended' }); setObsText(''); setObsPayment('Efectivo'); }}>
                      ✅ Atendida
                    </button>
                  )}
                  {detailModal.status !== 'not_attended' && (
                    <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => { setDetailModal(null); setObsModal({ appt: detailModal, nextStatus: 'not_attended' }); setObsText(''); }}>
                      ✗ No asistió
                    </button>
                  )}
                  <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => { setDetailModal(null); setRescheduleModal(detailModal); setRescheduleDate(''); }}>
                    📅 Reagendar
                  </button>
                  <button className="btn btn-danger" style={{ width: '100%' }} onClick={() => { setDetailModal(null); setCancelModal(detailModal); setCancelReason(''); }}>
                    ✕ Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal observaciones */}
      {obsModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{obsModal.nextStatus === 'attended' ? 'Marcar como Atendida' : 'Marcar como No asistió'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setObsModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Cliente: <strong>{obsModal.appt.client_name}</strong> — {obsModal.appt.start_time?.slice(0,5)} — {obsModal.appt.service_name}
                {obsModal.appt.price ? <> — <strong>{fmtCurrency(obsModal.appt.price)}</strong></> : ''}
              </p>
              {obsModal.nextStatus === 'attended' && (
                <div className="form-group">
                  <label className="form-label">Método de pago</label>
                  <select className="form-select" value={obsPayment} onChange={e => setObsPayment(e.target.value)}>
                    {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Observaciones (opcional)</label>
                <textarea className="form-textarea" rows={3} value={obsText} onChange={e => setObsText(e.target.value)} placeholder="Notas sobre el servicio o la visita..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setObsModal(null)}>Cancelar</button>
              <button className={`btn ${obsModal.nextStatus === 'attended' ? 'btn-primary' : 'btn-ghost'}`} onClick={handleObsConfirm}>
                {obsModal.nextStatus === 'attended' ? 'Confirmar asistencia' : 'Confirmar no asistencia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal reagendar */}
      {rescheduleModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Reagendar Cita</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setRescheduleModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                Reagendando cita de <strong>{rescheduleModal.client_name}</strong> — {rescheduleModal.service_name}
              </p>
              <div className="form-group">
                <label className="form-label">Nueva fecha *</label>
                <input type="date" className="form-input" value={rescheduleDate} min={todayStr} onChange={e => setRescheduleDate(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRescheduleModal(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={!rescheduleDate} onClick={handleReschedule}>Confirmar reagenda</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal cancelar */}
      {cancelModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Cancelar Cita</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setCancelModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Cancelando cita de <strong>{cancelModal.client_name}</strong> a las {cancelModal.start_time?.slice(0,5)}</p>
              <div className="form-group">
                <label className="form-label">Razón de cancelación *</label>
                <textarea className="form-textarea" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Motivo de cancelación..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelModal(null)}>Volver</button>
              <button className="btn btn-danger" disabled={!cancelReason.trim()}
                onClick={() => { changeStatus(cancelModal.id, 'cancelled', cancelReason); setCancelModal(null); }}>
                Confirmar cancelación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva cita */}
      {newModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <span className="modal-title">Nueva Cita — {selectedDate}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setNewModal(false)}>✕</button>
            </div>
            <form onSubmit={handleNewSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Nombre del cliente *</label>
                    <input className="form-input" required value={newForm.client_name} onChange={e => setNewForm(f => ({ ...f, client_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono *</label>
                    <input className="form-input" required value={newForm.client_phone} onChange={e => setNewForm(f => ({ ...f, client_phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Barbero *</label>
                    <select className="form-select" required value={newForm.worker_id} onChange={e => setNewForm(f => ({ ...f, worker_id: e.target.value, start_time: '' }))}>
                      <option value="">Seleccionar...</option>
                      {workers.filter(w => (w.role === 'barbero' || w.also_barber) && w.activo).map(w => (
                        <option key={w.id} value={w.id}>{w.nombre || w.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Servicio *</label>
                    <select className="form-select" required value={newForm.service_id} onChange={e => setNewForm(f => ({ ...f, service_id: e.target.value, start_time: '' }))}>
                      <option value="">Seleccionar...</option>
                      {services.filter(s => s.is_active).map(s => (
                        <option key={s.id} value={s.id}>{s.name} — {fmtCurrency(s.price)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {availableSlots.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Horario disponible *</label>
                    <div className="time-grid">
                      {availableSlots.map(slot => (
                        <div key={slot} className={`time-slot ${newForm.start_time === slot ? 'active' : ''}`}
                          onClick={() => setNewForm(f => ({ ...f, start_time: slot }))}>{slot}</div>
                      ))}
                    </div>
                  </div>
                )}
                {newForm.worker_id && newForm.service_id && availableSlots.length === 0 && (
                  <div className="alert alert-warning">No hay horarios disponibles para esta fecha y barbero.</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setNewModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !newForm.start_time}>
                  {saving ? <><div className="spinner" />Guardando...</> : '✅ Crear Cita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
